import React, { useEffect, useState, useCallback, useRef } from 'react';
import './Gallery.css';

const API = process.env.REACT_APP_API_URL;

const Gallery = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null); // null = closed, number = open
  const [bannerUrl, setBannerUrl] = useState(null);

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/gallery`);
      const data = await res.json();
      setMedia(data.items || []);
    } catch {
      setMedia([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBanner = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/gallery-banner`);
      const data = await res.json();
      setBannerUrl(data.bannerUrl || null);
    } catch {
      setBannerUrl(null);
    }
  }, []);

  useEffect(() => {
    fetchMedia();
    fetchBanner();
  }, [fetchMedia, fetchBanner]);

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const goPrev = useCallback(() => {
    setLightboxIndex((i) => (i > 0 ? i - 1 : media.length - 1));
  }, [media.length]);

  const goNext = useCallback(() => {
    setLightboxIndex((i) => (i < media.length - 1 ? i + 1 : 0));
  }, [media.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, goPrev, goNext]);

  // Touch swipe support
  const touchStartX = useRef(null);
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { diff > 0 ? goNext() : goPrev(); }
    touchStartX.current = null;
  };

  const lightboxItem = lightboxIndex !== null ? media[lightboxIndex] : null;

  return (
    <div className="gallery-page">

      {/* ── Full-width hero banner with real image overlay ── */}
      <div
        className="gallery-hero"
        style={bannerUrl ? { backgroundImage: `url(${bannerUrl})` } : undefined}
      >
        <div className="gallery-hero-overlay">
          <h1>Gallery</h1>
        </div>
      </div>

      {/* ── Grid section ── */}
      <div className="gallery-body">
        {loading ? (
          <div className="gallery-loading">
            <div className="g-spinner"></div>
            <p>Loading gallery...</p>
          </div>
        ) : media.length === 0 ? (
          <div className="gallery-empty">
            <div className="g-empty-icon">📷</div>
            <h3>No photos or videos yet</h3>
            <p>Check back soon — we update our gallery regularly.</p>
          </div>
        ) : (
          <div className="gallery-grid">
            {media.map((item, index) => (
              <div
                className="g-card"
                key={item.filename}
                onClick={() => openLightbox(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && openLightbox(index)}
              >
                {item.type === 'photo' ? (
                  <img
                    src={`${API}/gallery/${item.filename}`}
                    alt={item.caption || item.filename}
                    loading="lazy"
                    className="g-card-img"
                  />
                ) : (
                  <>
                    {item.thumbnail ? (
                      <img
                        src={`${API}/gallery/${item.thumbnail}`}
                        alt={item.caption || 'Video thumbnail'}
                        loading="lazy"
                        className="g-card-img"
                      />
                    ) : (
                      <video
                        src={`${API}/gallery/${item.filename}`}
                        muted
                        preload="metadata"
                        className="g-card-img"
                      />
                    )}
                    <div className="g-play-btn">▶</div>
                    <div className="g-video-badge">VIDEO</div>
                  </>
                )}
                {item.caption && (
                  <div className="g-card-caption">{item.caption}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Lightbox ── */}
      {lightboxItem && (
        <div
          className="lightbox"
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            {/* Close */}
            <button className="lightbox-close" onClick={closeLightbox}>✕</button>

            {/* Counter */}
            <div className="lightbox-counter">{lightboxIndex + 1} / {media.length}</div>

            {/* Prev */}
            <button
              className="lightbox-nav lightbox-prev"
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              aria-label="Previous"
            >&#8249;</button>

            {/* Media */}
            {lightboxItem.type === 'photo' ? (
              <img
                key={lightboxItem.filename}
                src={`${API}/gallery/${lightboxItem.filename}`}
                alt={lightboxItem.caption || lightboxItem.filename}
                className="lightbox-img"
              />
            ) : (
              <video
                key={lightboxItem.filename}
                src={`${API}/gallery/${lightboxItem.filename}`}
                controls
                autoPlay
                className="lightbox-video"
              />
            )}

            {/* Next */}
            <button
              className="lightbox-nav lightbox-next"
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              aria-label="Next"
            >&#8250;</button>

            {lightboxItem.caption && (
              <p className="lightbox-caption">{lightboxItem.caption}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
